import React, { useRef } from 'react';
import { gsap } from 'gsap';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import './CircularNav.css';
import { isMobile, isTablet } from 'react-device-detect';

const RADIUS = 280;
const MAX_ANGLE_SPACE = 25;
const START_ANGLE = 13;
const BASE_ANGLE = -180;

const WIDTH_BREAK_POINT = 980;

const CircularNav = ({
  data,
  isShown,
  buttonId,
  onClickHandler,
  onCloseHandler,
  isShowingBackButton,
  currentGallery,
}) => {
  const background = useRef(null);
  const lines = useRef(null);
  const buttons = useRef(null);
  const prevIsMenuShownRef = useRef();
  const isShowingBackButtonRef = useRef();
  const dataLengthRef = useRef(data.length);

  const getKey = (item) => {
    return 'id' in item ? item.id : item.name;
  };

  const linesList = data.map((item, i) => {
    return (
      <div
        className="home-gallery-line"
        key={`line${getKey(item)}`}
        index={i}
      />
    );
  });

  const buttonsList = data.map((item, i) => {
    const title = 'title' in item ? item.title : item.name;
    const words = title.split(' ');
    let additionalClass = '';
    for (const word of words) {
      if (word.length > 7) {
        additionalClass = 'long-title';
      }
      if (word.length > 10) {
        additionalClass = 'vlong-title';
        break;
      }
    }
    const currentKey =
      currentGallery && currentGallery.meta
        ? getKey(currentGallery.meta)
        : null;
    const itemKey = getKey(item);
    const className = `home-gallery-button ${
      currentKey === itemKey ? 'active' : ''
    } ${additionalClass}`;
    return (
      <button
        key={itemKey}
        index={i}
        type="button"
        className={className}
        onClick={() => {
          onClickHandler(item);
        }}
      >
        <div className="home-gallery-button-txt-container">
          <div className="home-gallery-button-title">{title}</div>
          <div className="home-gallery-button-subtitle">{item.subtitle}</div>
        </div>
      </button>
    );
  });

  React.useLayoutEffect(() => {
    const layout = (isMenuShown = false) => {
      const isSimplified = !!(
        window.innerWidth < WIDTH_BREAK_POINT ||
        isMobile ||
        isTablet
      );

      const lineElements = lines.current.getElementsByTagName('div');
      const buttonElements = buttons.current.getElementsByTagName('button');
      const baseElement = document.getElementById(buttonId);
      const center = {
        x: isSimplified ? window.innerWidth / 2 : baseElement.offsetLeft,
        y: baseElement.offsetTop - 50,
      };

      const lineScale = isMenuShown && !isSimplified ? 1 : 0;
      const ease = isMenuShown ? 'back.out' : 'back.in';
      let startAngle = START_ANGLE;
      let angleSpace = (180 - startAngle * 2) / (buttonElements.length - 1);

      if (angleSpace > MAX_ANGLE_SPACE) {
        angleSpace = MAX_ANGLE_SPACE;
        startAngle = (angleSpace * (buttonElements.length - 1) - 180) / -2;
      }

      let lineAngle = BASE_ANGLE + startAngle;
      let angle = lineAngle * (Math.PI / 180); // radians
      let backgroundRadius = 0;
      let delay = 0;
      let i = 0;

      const duration = isMenuShown ? 0.4 : 0.3;
      const delayStep = isMenuShown ? 0.05 : 0.04;

      // mobile :
      let columnIndex = 0;
      let lineIndex = 0;
      const margin = 15;
      const columnCount = 3;
      const columnWidth = Math.min(
        (window.innerWidth - margin) / columnCount,
        125,
      );
      const columnElementCount = Math.floor(
        buttonElements.length / columnCount,
      );
      const reducedRadius = Math.min(100, columnWidth - margin);
      let off = 0;
      if (
        (columnElementCount + 1) * (reducedRadius + margin) >
        window.innerHeight - 150
      ) {
        off = -100;
      }
      // ----

      for (const el of buttonElements) {
        const circleX = RADIUS * Math.cos(angle);
        const circleY = RADIUS * Math.sin(angle);
        let x = isMenuShown ? center.x + circleX : center.x;
        let y = isMenuShown ? center.y + circleY : center.y;
        lineAngle = Math.floor(lineAngle);

        gsap.killTweensOf(lineElements[i]);
        gsap.killTweensOf(el);

        if (isSimplified) {
          const isOdd = columnIndex % 2 ? true : false;
          const offset = !isOdd ? off : reducedRadius / 2 + off;
          const limit = !isOdd ? columnElementCount : columnElementCount + 1;
          x =
            window.innerWidth / 2 -
            columnWidth * (columnElementCount / 2) +
            columnWidth * columnIndex +
            margin;
          y =
            window.innerHeight -
            offset -
            10 -
            (columnElementCount + 1) * (reducedRadius + margin) +
            lineIndex * (reducedRadius + margin);

          gsap.set(el, {
            width: reducedRadius,
            height: reducedRadius,
          });

          lineIndex++;
          if (lineIndex >= limit) {
            lineIndex = 0;
            columnIndex++;
          }
        }

        if (isMenuShown) {
          gsap.set(lineElements[i], {
            width: RADIUS - 60,
            x: center.x,
            y: center.y,
          });
        }
        gsap.to(lineElements[i], {
          x: center.x,
          y: center.y,
          delay: delay * 0.9,
          duration: duration * 0.8,
          ease: 'circ.out',
          scaleX: lineScale,
          rotation: lineAngle,
        });
        gsap.to(el, {
          duration: duration,
          x: x,
          y: y,
          delay: delay,
          ease,
          onStart: () => {
            if (isMenuShown) {
              gsap.set(el, { autoAlpha: 1 });
            }
          },
          onComplete: () => {
            if (!isMenuShown) {
              gsap.set(el, { autoAlpha: 0 });
            }
          },
        });
        lineAngle += angleSpace;
        angle += angleSpace * (Math.PI / 180);
        delay += delayStep;
        backgroundRadius = Math.max(
          Math.max(backgroundRadius, Math.abs(circleX)),
          Math.abs(circleY),
        );
        i++;
      }

      backgroundRadius += 100 + off;

      // backgroundRadius =
      //  Math.ceil(Math.max(backgroundRadius, window.innerHeight / 2)) + 100;
      gsap.killTweensOf(background.current);
      gsap.set(background.current, {
        width: backgroundRadius * 2,
        height: backgroundRadius * 2,
        top: Math.floor(center.y),
        left: Math.floor(center.x),
      });

      gsap.to(background.current, {
        scale: isMenuShown ? 1 : 0,
        duration: isMenuShown ? 0.4 : 0.5,
        delay: isMenuShown ? 0 : 0.2,
        ease: isMenuShown ? 'back.out' : 'back.inOut',
        onStart: () => {
          if (isMenuShown) {
            gsap.set(background.current, { autoAlpha: 1 });
          }
        },
      });
    };

    const hasMenuChanged =
      prevIsMenuShownRef.current !== isShown ||
      dataLengthRef.current !== data.length ||
      isShowingBackButtonRef.current !== isShowingBackButton;
    const resizeListener = () => {
      if (hasMenuChanged) {
        layout(isShown, true);
      }
    };
    const handleDown = (event) => {
      if (
        isShown &&
        (event.target.tagName.toLowerCase() === 'canvas' ||
          event.target.id === 'container-details-artworks' ||
          event.target.id === 'about-toggle' ||
          event.target.classList.contains('overlay') ||
          event.target.id === buttonId)
      ) {
        onCloseHandler();
      }
    };
    if (hasMenuChanged) {
      layout(isShown, true);
    }
    window.addEventListener('resize', resizeListener);
    if (isShown) {
      window.addEventListener('mousedown', handleDown);
      window.addEventListener('touchend', handleDown);
    }
    prevIsMenuShownRef.current = isShown;
    dataLengthRef.current = data.length;
    isShowingBackButtonRef.current = isShowingBackButton;
    return () => {
      window.removeEventListener('mousedown', handleDown);
      window.removeEventListener('touchend', handleDown);
      window.removeEventListener('resize', resizeListener);
    };
  }, [
    isShown,
    data,
    isShowingBackButton,
    currentGallery,
    onCloseHandler,
    buttonId,
  ]);

  return (
    <>
      <div className="circular-menu-background" ref={background} />
      <ul ref={lines}>
        {isShowingBackButton && (
          <div className="home-gallery-line" key="line-back" index="-1" />
        )}
        {linesList}
      </ul>
      <ul ref={buttons}>
        {isShowingBackButton && (
          <button
            key="back"
            index="-1"
            type="button"
            className="home-gallery-button home-gallery-button-back"
            onClick={() => {
              onCloseHandler();
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </button>
        )}
        {buttonsList}
      </ul>
    </>
  );
};

export default CircularNav;
